import { flatten, sortBy, uniq } from "ramda";
import { type InjectedFormProps } from "redux-form";
import { formatDateTimeFromISO, parseDateTimeToISO } from "widgets";

import { ATTRIBUTE_TYPE, COLUMN_SET } from "@/settings/settings.ts";

import type WebApiClient from "../api/WebApiClient.js";
import {
  type Attribute,
  type AttributeDefaultValue,
  type InputAttributeValueInput,
  type ParentType,
  type SampleType,
} from "../graphql.js";
import { type IEditable, type IEditCellChange } from "../redux/modules/data-editing.js";
import {
  findAttributeByKey,
  type IAttributeDefaultValueLookup,
  type IAttributeLookup,
  type IAttributesTemplatesLookup,
  type IDefaultValuesLookup,
  type ISampleEditingState,
  type ISampleTypeAttributesTemplatesLookup,
} from "../redux/modules/sample-editing.js";
import { type IAppState } from "../redux/reducer.js";
import { countMacroRegex, nextMacroRegex, scriptAttributeRegex } from "../ui-modules/core/forms/ScriptInputField.js";
import { type IPlaybookWizardStepInjectedProps } from "../ui-modules/playbooks/PlaybookWizardStep.js";
import { createAttributeValueInputs, getPrimaryParentType, getUniqueAttributeId } from "./sample.js";
import { type TGridSelModelIdentifier } from "./selection-model.js";

enum ETemplatePlaceholderTypes {
  ATT_ID, // {\d+}
  NEXT, // {NEXT(attributes=[comma-sep-id-list],sequence?='chars-in-sequence')}
  COUNT, // {COUNT(attributes=[comma-sep-id-list])}
  INVALID, // unknown ones
}

// representation of all parameters needed to resolve a template value, computed once at initialization
export interface ISampleTemplateParameter {
  start?: number;
  stop?: number;
  templateString: string;
  templateType: ETemplatePlaceholderTypes;
  attributes: Attribute[];
  sampleTypeId?: number; // necessary for some resolvers
  parameters?: any;
}

interface ITemplateValueResolvingPromise {
  sampleTemplateParameters: ISampleTemplateParameter;
  promise: Promise<string>;
}

// representation of the asynchronous pending template resolver, computed each time the value needs to be resolved
export interface ISampleTemplateResolver {
  rowId: TGridSelModelIdentifier;
  attributeKey: string;
  attribute: Attribute; // the template attribute
  resolvers: ITemplateValueResolvingPromise[];
}

interface IResolvedSamplePlaceholder {
  resolvedValue: string;
  sampleTemplateParameters: ISampleTemplateParameter;
}

export function removeAllTemplatePlaceholders(defaultValue: string): string {
  return defaultValue.replace(scriptAttributeRegex, "");
}

export function getSampleTemplateResolvers(
  templatedAttributeKeys: string[],
  attributesTemplatesLookup: IAttributesTemplatesLookup,
  attributeLookup: IAttributeLookup,
  rowId: TGridSelModelIdentifier,
  dependentValueGetter: (attribute: Attribute) => any,
) {
  return (dispatch, _getState: () => IAppState, _webApiClient: WebApiClient): ISampleTemplateResolver[] => {
    return templatedAttributeKeys.map((templatedAttributeKey: string) => {
      const sampleTemplateResolver: ISampleTemplateResolver = {
        rowId: rowId,
        attributeKey: templatedAttributeKey,
        attribute: attributeLookup[templatedAttributeKey],
        resolvers: dispatch(resolveTemplate(templatedAttributeKey, attributesTemplatesLookup, dependentValueGetter)),
      };

      return sampleTemplateResolver;
    });
  };
}

function getAttributeHumanReadableValueAsTemplateReference(attribute: Attribute, value: any) {
  const attributeTypeId = attribute.attributeType.id;

  switch (attributeTypeId) {
    case ATTRIBUTE_TYPE.CONTROLLED_TEXT:
    case ATTRIBUTE_TYPE.DYNAMIC_TERMS:
      return value?.label || value; // depending on the single or batch mode, the store contains the label or full object
    case ATTRIBUTE_TYPE.DATE:
      // the date value is not necessarily parseable, it might not be ISO formatted
      return formatDateTimeFromISO(parseDateTimeToISO(value as string), { hideTime: true });
    case ATTRIBUTE_TYPE.BOOLEAN:
      return value === true ? "YES" : "NO";
    case ATTRIBUTE_TYPE.DERIVED_TERMS:
      return ""; // this does not work, since derived term is dynamic and resolved on the display part, not during registration/editing
    case ATTRIBUTE_TYPE.FILE:
      return ""; // this should not be possible
    default:
      return value;
  }
}

export function computeGridTemplatedValues(changes: IEditCellChange[], sampleType: SampleType) {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient): Promise<IEditCellChange[]> => {
    const sampleTemplateResolversForAllModifiedRows: ISampleTemplateResolver[] = [];
    const registrationState: ISampleEditingState = getState().sampleEditingState;
    const modifiedAttributeKeys: string[] = uniq<string>(
      changes.map((change: IEditCellChange) => String(change.column.columnId)),
    ); // column.attribute is not always available
    const attributesToRefresh: string[] = getAttributesToRefreshWhenTemplatedAttributesChanged(
      modifiedAttributeKeys,
      registrationState.templatesLookup[sampleType.id],
    );

    // we apply the following logic only if there are some attributes having template default values with placeholders
    if (attributesToRefresh.length > 0) {
      // we create a list of unique sample ids that have changed
      const modifiedRowIds: any[] = uniq(changes.map((change: IEditCellChange) => change.rowId));

      // we loop through the modified rows in order to compute the template values
      modifiedRowIds.forEach((rowId: TGridSelModelIdentifier) => {
        // the current row should be up to date with the pending changes
        const gridRow: IEditable = registrationState.editingGrid.find((row) => row.id == rowId);
        const sampleTemplateResolvers: ISampleTemplateResolver[] = dispatch(
          getSampleTemplateResolvers(
            attributesToRefresh,
            registrationState.templatesLookup[sampleType.id],
            registrationState.attributeLookup,
            rowId,
            (attribute: Attribute) =>
              getAttributeHumanReadableValueAsTemplateReference(
                attribute,
                gridRow[attribute.id], //?.value,
              ),
          ),
        );

        // we push the new resolvers regarding template values into the dedicated array
        sampleTemplateResolversForAllModifiedRows.push(...sampleTemplateResolvers);
      });
    }

    if (sampleTemplateResolversForAllModifiedRows.length > 0) {
      const templateChangePromises: Promise<IEditCellChange>[] = sampleTemplateResolversForAllModifiedRows.map(
        (sampleTemplateResolver: ISampleTemplateResolver) => {
          return getValueFromTemplateResolver(sampleTemplateResolver, registrationState.defaultValuesLookup).then(
            (resolvedTemplate: string) => {
              const templateChange: IEditCellChange = {
                rowId: sampleTemplateResolver.rowId,
                column: { columnId: sampleTemplateResolver.attributeKey },
                newValue: resolvedTemplate,
              };

              return templateChange;
            },
          );
        },
      );

      return Promise.all(templateChangePromises);
    }

    return Promise.resolve([]); // since there are only the original changes (no template related changes) we return []
    // because the original changes have been dispatched already
  };
}

function getValueFromTemplateResolver(
  sampleTemplateResolver: ISampleTemplateResolver,
  defaultValuesLookup: IDefaultValuesLookup,
): Promise<string> {
  return Promise.all(
    sampleTemplateResolver.resolvers.map((resolver: ITemplateValueResolvingPromise) => {
      return resolver.promise.then((resolvedValue: string) => ({
        resolvedValue: resolvedValue,
        sampleTemplateParameters: resolver.sampleTemplateParameters,
      }));
    }),
  ).then((results: IResolvedSamplePlaceholder[]) => {
    const resolvedTemplate: string = defaultValuesLookup[sampleTemplateResolver.attributeKey];

    return assembleTemplateValue(results, resolvedTemplate);
  });
}

function assembleTemplateValue(results: IResolvedSamplePlaceholder[], rawTemplate: string): string {
  if (results.length === 0) {
    return rawTemplate;
  }
  let resolvedTemplate: string = rawTemplate.slice();

  // sort by descending position so that multiple string replacements is not messed up
  const sortedPlaceholders = sortBy((o) => -o.sampleTemplateParameters.start!, results);
  sortedPlaceholders.forEach((resolvedPlaceholder) => {
    resolvedTemplate =
      resolvedTemplate.substring(0, resolvedPlaceholder.sampleTemplateParameters.start) +
      resolvedPlaceholder.resolvedValue +
      resolvedTemplate.substring(resolvedPlaceholder.sampleTemplateParameters.stop, resolvedTemplate.length);
  });

  return resolvedTemplate;
}

function getAttributesFromTemplate(attributeIdsParsedFromTemplate: string[], sampleType: SampleType): Attribute[] {
  const primaryParentType: ParentType = getPrimaryParentType(sampleType);

  return attributeIdsParsedFromTemplate
    .map((templatesAttributeIdAsString: string) => {
      const templatesAttributeId = Number(templatesAttributeIdAsString);
      const dependentAttribute: Attribute =
        sampleType.sampleAttributes.find((a: Attribute) => a.id === templatesAttributeId) ||
        (primaryParentType && primaryParentType.parentAttributes.find((a: Attribute) => a.id === templatesAttributeId));

      if (dependentAttribute) {
        return dependentAttribute;
      } else {
        // `Warning: Column ${templatesAttributeId} is referenced by an attribute template, but it is not found in the list of sample or primary parent attributes. Is this column set to "not modifiable" or "automatic"?`,
        return null;
      }
    })
    .filter((dependentAttribute: Attribute) => !!dependentAttribute);
}

export function resolveTemplate(
  templatedAttributeKey: string,
  attributesTemplatesLookup: IAttributesTemplatesLookup,
  dependentValueGetter: (attribute: Attribute) => any,
) {
  return (_dispatch, _getState: () => IAppState, webApiClient: WebApiClient): ITemplateValueResolvingPromise[] => {
    // each of the attribute having template values might have multiple placeholders
    return attributesTemplatesLookup[templatedAttributeKey].map(
      (sampleTemplateParameters: ISampleTemplateParameter) => {
        if (sampleTemplateParameters.templateType === ETemplatePlaceholderTypes.ATT_ID) {
          const dependentAttribute: Attribute = sampleTemplateParameters.attributes[0]; // array might be empty because we could not decode the dependent attribute(s)

          return {
            sampleTemplateParameters: sampleTemplateParameters,
            promise: Promise.resolve((dependentAttribute && dependentValueGetter(dependentAttribute)) || ""),
          };
        } else if (sampleTemplateParameters.templateType === ETemplatePlaceholderTypes.NEXT) {
          const attributeInputs: InputAttributeValueInput[] = createAttributeValueInputs(
            sampleTemplateParameters.attributes,
            (attribute) => dependentValueGetter(attribute) || "",
          );

          return {
            sampleTemplateParameters: sampleTemplateParameters,
            promise: sampleTemplateParameters.parameters.sequence
              ? webApiClient.resolvePlaceholderNextElement(
                  sampleTemplateParameters.sampleTypeId,
                  attributeInputs,
                  sampleTemplateParameters.parameters.sequence,
                  sampleTemplateParameters.parameters.loopOverSequence,
                )
              : webApiClient.resolvePlaceholderNext(sampleTemplateParameters.sampleTypeId, attributeInputs),
          };
        } else if (sampleTemplateParameters.templateType === ETemplatePlaceholderTypes.COUNT) {
          const attributeInputs: InputAttributeValueInput[] = createAttributeValueInputs(
            sampleTemplateParameters.attributes,
            (attribute) => dependentValueGetter(attribute) || "",
          );

          return {
            sampleTemplateParameters: sampleTemplateParameters,
            promise: webApiClient.resolvePlaceholderCount(sampleTemplateParameters.sampleTypeId, attributeInputs),
          };
        }

        throw new Error(`Sample Template not supported '${sampleTemplateParameters.templateString}'`);
      },
    );
  };
}

function getTemplateMacroOptionValueAsArray(template: string, optionName: string, mandatory = true): string[] {
  const attributesOptionMatchingParams: RegExpMatchArray = template.match(
    new RegExp(`.*${optionName}=[[](.*?)[\\]].*`),
  );

  if (mandatory && !attributesOptionMatchingParams) {
    throw new Error(
      `Template option ${optionName} could no be found in template ${template}, please contact your administrator to fix the configuration.`,
    );
  }

  const listOfAttributesAsString: string = attributesOptionMatchingParams && attributesOptionMatchingParams[1];

  return (
    listOfAttributesAsString && listOfAttributesAsString.split(",").map((attributeId: string) => attributeId.trim())
  );
}

function getTemplateMacroOptionValueAsBoolean(template: string, optionName: string, mandatory = true): boolean {
  const attributesOptionMatchingParams: RegExpMatchArray = template.match(new RegExp(`.*${optionName}=(true|false).*`));

  if (mandatory && !attributesOptionMatchingParams) {
    throw new Error(
      `Template option ${optionName} could no be found in template ${template}, please contact your administrator to fix the configuration.`,
    );
  }

  const booleanAttributesAsString: string = attributesOptionMatchingParams && attributesOptionMatchingParams[1];

  return booleanAttributesAsString === "true";
}

interface IResolveFormFieldTemplatesProps extends IPlaybookWizardStepInjectedProps, InjectedFormProps<FormData, any> {
  sampleEditingState: ISampleEditingState;
  templatesLookup: IAttributesTemplatesLookup;
}

// factored out because used in both sample form and primary parent form, with redux-form's onChange handler parameters
export function resolveFormFieldTemplatesDynamically(...params: any[]) {
  // because the typescript definition is not up to date !
  const newValues = params[0]; // The changed field values in the form of { field1: 'value1', field2: 'value2' }.
  const dispatch = params[1];
  const props: IResolveFormFieldTemplatesProps = params[2];
  const previousValues = params[3];

  if (!props.templatesLookup) {
    return;
  }

  // get the list of changed attributes, so that we optimize the templates to be resolved
  const changedFieldAttributeKeys: string[] = Object.keys(newValues).filter(
    (attributeKey: string) => !previousValues[attributeKey] || previousValues[attributeKey] !== newValues[attributeKey],
  );
  const attributesWhichDependentTemplateAttributesChanged: string[] =
    getAttributesToRefreshWhenTemplatedAttributesChanged(changedFieldAttributeKeys, props.templatesLookup);

  const sampleTemplateResolvers: ISampleTemplateResolver[] = dispatch(
    getSampleTemplateResolvers(
      attributesWhichDependentTemplateAttributesChanged,
      props.templatesLookup,
      props.sampleEditingState.attributeLookup,
      null,
      (attribute: Attribute) =>
        getAttributeHumanReadableValueAsTemplateReference(attribute, newValues[getUniqueAttributeId(attribute)]),
    ),
  );

  // async errors are persistent until you touch the field, therefore in case a template field is changed
  // we need to reset the async errors for the dependent attributes even if they have not been touched
  attributesWhichDependentTemplateAttributesChanged.forEach((attributeKey: string) =>
    dispatch(props.clearAsyncError(attributeKey)),
  );

  sampleTemplateResolvers.forEach((sampleTemplateResolver: ISampleTemplateResolver) =>
    getValueFromTemplateResolver(sampleTemplateResolver, props.sampleEditingState.defaultValuesLookup)
      .then((resolvedTemplate: string) => {
        dispatch(props.change(sampleTemplateResolver.attributeKey, resolvedTemplate));
      })
      .catch((error: Error) => {
        props.updateWizardMessage(error.message);
      }),
  );
}

function preparseTemplatedValue(value: string, sampleType: SampleType): ISampleTemplateParameter[] {
  // Take the template value text and build a list of ISampleTemplateParameters of all valid placeholders

  const sampleTemplateParameters: ISampleTemplateParameter[] = [];

  // STANDARD ATTRIBUTE ID PLACEHOLDER
  const attributesIdMatching = [...value.matchAll(scriptAttributeRegex)];

  if (attributesIdMatching.length) {
    attributesIdMatching.forEach((attributeIdMatching) => {
      const attributeId = attributeIdMatching[1];
      const templateString: string = attributeIdMatching[0];
      // parse default value at location and place its parameters to a location structure
      const location = { start: attributeIdMatching.index, stop: attributeIdMatching.index + templateString.length };
      const dependentAttributes: Attribute[] = getAttributesFromTemplate([attributeId], sampleType);

      sampleTemplateParameters.push({
        ...location,
        templateString: templateString,
        templateType: ETemplatePlaceholderTypes.ATT_ID,
        attributes: dependentAttributes,
      });
    });
  }

  // NEXT MACRO (with options)
  const nextMacrosMatching = [...value.matchAll(nextMacroRegex)];
  if (nextMacrosMatching.length) {
    nextMacrosMatching.forEach((nextMacroMatching) => {
      const macro = nextMacroMatching[1];
      const templateString: string = nextMacroMatching[0];
      // parse default value at location and place its parameters to a location structure
      const location = { start: nextMacroMatching.index, stop: nextMacroMatching.index + templateString.length };
      const attributeIdsParsedFromTemplate: string[] = getTemplateMacroOptionValueAsArray(macro, "attributes");
      const sequenceParsedFromTemplate: string[] = getTemplateMacroOptionValueAsArray(macro, "sequence", false);
      const loopOverSequenceParsedFromTemplate: boolean = getTemplateMacroOptionValueAsBoolean(
        macro,
        "loopOverSequence",
        false,
      );
      const dependentAttributes: Attribute[] = getAttributesFromTemplate(attributeIdsParsedFromTemplate, sampleType);
      sampleTemplateParameters.push({
        ...location,
        templateString: templateString,
        templateType: ETemplatePlaceholderTypes.NEXT,
        sampleTypeId: sampleType.id,
        attributes: dependentAttributes,
        parameters: {
          sequence: sequenceParsedFromTemplate,
          loopOverSequence: loopOverSequenceParsedFromTemplate,
        },
      });
    });
  }

  // NEXT MACRO (with options)
  const countMacrosMatching = [...value.matchAll(countMacroRegex)];
  if (countMacrosMatching.length) {
    countMacrosMatching.forEach((countMacroMatching) => {
      const macro = countMacroMatching[1];
      const templateString: string = countMacroMatching[0];
      // parse default value at location and place its parameters to a location structure
      const location = { start: countMacroMatching.index, stop: countMacroMatching.index + templateString.length };
      const attributeIdsParsedFromTemplate: string[] = getTemplateMacroOptionValueAsArray(macro, "attributes");
      const dependentAttributes: Attribute[] = getAttributesFromTemplate(attributeIdsParsedFromTemplate, sampleType);

      sampleTemplateParameters.push({
        ...location,
        templateString: templateString,
        templateType: ETemplatePlaceholderTypes.COUNT,
        sampleTypeId: sampleType.id,
        attributes: dependentAttributes,
      });
    });
  }

  return sampleTemplateParameters;
}

export function computeTemplatesLookup(
  sampleType: SampleType,
  defaultValues: IAttributeDefaultValueLookup,
): ISampleTypeAttributesTemplatesLookup {
  const templatesLookup: IAttributesTemplatesLookup = {};
  const primaryParentType: ParentType = getPrimaryParentType(sampleType);

  Object.keys(defaultValues).map((attributeKey: string) => {
    const defaultValue: AttributeDefaultValue = defaultValues[attributeKey];

    if (defaultValue.template) {
      // since the defaultValues map does index by attribute id (versus unique key) we find back the corresponding attribute
      const templatedAttribute: Attribute = findAttributeByKey(attributeKey, sampleType, primaryParentType);
      const templateParams: ISampleTemplateParameter[] = preparseTemplatedValue(defaultValue.template, sampleType);

      if (templateParams?.length) {
        templatesLookup[getUniqueAttributeId(templatedAttribute)] = templateParams;
      }
    }
  });

  return { [sampleType.id]: templatesLookup };
}

// retrieve the list of attributes for which the templated value needs to be refreshed because their dependent attributes have been touched
function getAttributesToRefreshWhenTemplatedAttributesChanged(
  changedFieldAttributeKeys: string[],
  templatesLookup: IAttributesTemplatesLookup,
): string[] {
  return Object.keys(templatesLookup).filter((attributeKey: string) => {
    return !!templatesLookup[attributeKey].find(
      (sampleTemplateParameters: ISampleTemplateParameter) =>
        !!sampleTemplateParameters.attributes.find(
          (attribute: Attribute) =>
            !!changedFieldAttributeKeys.find((key: string) => key === getUniqueAttributeId(attribute)),
        ),
    );
  });
}

// return unique list of attributes which are required to resolve the current templated attribute
export function getTemplateDependentAttributes(
  templatesLookup: IAttributesTemplatesLookup,
  attributeKey: string,
): Attribute[] {
  return uniq(
    flatten(
      (templatesLookup[attributeKey] || []).map(
        (sampleTemplateParameters: ISampleTemplateParameter) => sampleTemplateParameters.attributes,
      ),
    ),
  );
}

export function isAttributeAutomatic(attribute: Attribute, templatesLookup: IAttributesTemplatesLookup) {
  if (!templatesLookup) {
    return false;
  }
  return attribute.automatic || !!templatesLookup[getUniqueAttributeId(attribute)];
}

export function filterTemplateSuggestions(templateAttributes: Attribute[], currentAttribute: Attribute): Attribute[] {
  return templateAttributes
    .filter((templateAttribute: Attribute) => templateAttribute.id !== currentAttribute.id) // not self
    .filter((templateAttribute: Attribute) => templateAttribute.columnSet.id !== COLUMN_SET.COS_ANALYTICAL_ID.id) // not analytical attribute, will not be filled at sample registration
    .filter((templateAttribute: Attribute) => !templateAttribute.automatic && !!templateAttribute.modifiable); // should NOT be automatic, but should be modifiable (available in registration form)
}
